import React, { useEffect, useState } from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	filePreviewImage: {
		maxWidth: '100%',
		maxHeight: '70vh',
		objectFit: 'contain',
		margin: '0 auto',
		display: 'block',
	},

	filePreviewFallback: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
		minHeight: '200px',
	},

	filePreviewTextContent: {
		maxHeight: '80vh',
		overflow: 'auto',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		font: token('font.code'),
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},

	filePreviewLoadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px',
	},
});

function isImageFileName(fileName: string): boolean {
	return /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(fileName);
}

const TEXT_EXTENSIONS = [
	'.txt',
	'.md',
	'.markdown',
	'.json',
	'.xml',
	'.yaml',
	'.yml',
	'.js',
	'.jsx',
	'.ts',
	'.tsx',
	'.css',
	'.scss',
	'.less',
	'.html',
	'.htm',
	'.csv',
	'.log',
	'.sh',
	'.bash',
	'.py',
	'.rb',
	'.java',
	'.c',
	'.cpp',
	'.h',
	'.go',
	'.rs',
	'.sql',
];

function getBlobPreviewKindFromFileName(fileName: string): 'image' | 'pdf' | 'text' | 'unknown' {
	const base = fileName.split(/[/\\]/).pop() ?? fileName;
	const lower = base.toLowerCase();
	if (isImageFileName(lower)) {
		return 'image';
	}

	if (TEXT_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
		return 'text';
	}
	return 'unknown';
}

/** Headers for authenticated Rovo file/blob fetches (gateway). `x-cloudid` is omitted when absent. */
export function buildRovoFileFetchHeaders(cloudId?: string): Record<string, string> {
	const headers: Record<string, string> = {
		'x-product': 'rovo',
		'x-experience-id': 'ai-mate',
	};
	if (cloudId) {
		headers['x-cloudid'] = cloudId;
	}
	return headers;
}

export type ScopeFileBlobPreviewModalProps = {
	isOpen: boolean;
	onClose: () => void;
	fileUrl: string;
	fileName: string;
	formatMessage: IntlShape['formatMessage'];
	/** i18n copy for non-previewable file types (e.g. PDF). */
	filePreviewFallbackDescription: MessageDescriptor;
	/** Cloud / site id for gateway `x-cloudid` on file preview fetches. */
	cloudId?: string;
};

/**
 * Modal that previews scope file / image blobs via authenticated `fetch` (gateway URLs),
 * including base64 text responses for images.
 */
export function ScopeFileBlobPreviewModal({
	isOpen,
	onClose,
	fileUrl,
	fileName,
	formatMessage,
	filePreviewFallbackDescription,
	cloudId,
}: ScopeFileBlobPreviewModalProps): JSX.Element {
	const kind = getBlobPreviewKindFromFileName(fileName);
	const [textContent, setTextContent] = useState<string | null>(null);
	const [isLoadingText, setIsLoadingText] = useState(false);
	const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);

	// Fetch text files with auth headers
	useEffect(() => {
		if (!isOpen || kind !== 'text') {
			return;
		}
		let cancelled = false;
		setIsLoadingText(true);
		fetch(fileUrl, {
			method: 'GET',
			headers: buildRovoFileFetchHeaders(cloudId),
			credentials: 'include',
		})
			.then((res) => res.text())
			.then((text) => {
				if (!cancelled) {
					setTextContent(text);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setTextContent(null);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoadingText(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, kind, fileUrl, cloudId, fileName]);

	// Fetch images as blobs with auth headers (browsers don't support custom headers on <img src>).
	// We convert to a data URL via FileReader to satisfy CSP img-src policies.
	useEffect(() => {
		if (!isOpen || kind !== 'image') {
			return;
		}
		let cancelled = false;
		const ext = (fileName.split('.').pop() ?? '').toLowerCase();
		const MIME_BY_EXT: Record<string, string> = {
			jpg: 'image/jpeg',
			jpeg: 'image/jpeg',
			png: 'image/png',
			gif: 'image/gif',
			webp: 'image/webp',
			svg: 'image/svg+xml',
			bmp: 'image/bmp',
			ico: 'image/x-icon',
			avif: 'image/avif',
		};
		const mimeType = MIME_BY_EXT[ext] ?? 'application/octet-stream';

		type ImagePreviewPayload = { kind: 'blob'; blob: Blob } | { kind: 'text'; text: string };

		fetch(fileUrl, {
			method: 'GET',
			headers: buildRovoFileFetchHeaders(cloudId),
			credentials: 'include',
		})
			.then(async (res): Promise<ImagePreviewPayload> => {
				if (!res.ok) {
					throw new Error(`[ImagePreview] fetch failed with status ${res.status}`);
				}
				const contentType = res.headers.get('content-type') ?? '';
				const looksLikeBinary = contentType.startsWith('image/');
				if (looksLikeBinary) {
					const blob = await res.blob();
					return { kind: 'blob' as const, blob };
				}
				const text = await res.text();
				return { kind: 'text' as const, text };
			})
			.then((payload): Promise<string | null> => {
				if (cancelled) {
					return Promise.resolve(null);
				}
				if (payload.kind === 'text') {
					const b64 = payload.text.trim();
					return Promise.resolve(`data:${mimeType};base64,${b64}`);
				}
				const correctBlob = new Blob([payload.blob], { type: mimeType });
				return new Promise<string>((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result as string);
					reader.onerror = (e) => reject(e);
					reader.readAsDataURL(correctBlob);
				});
			})
			.then((dataUrl) => {
				if (!cancelled && dataUrl !== null) {
					setImageBlobUrl(dataUrl);
				}
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('[ImagePreview] pipeline error:', err);
				if (!cancelled) {
					setImageBlobUrl(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, kind, fileUrl, cloudId, fileName]);

	const renderBody = () => {
		if (kind === 'image') {
			if (!imageBlobUrl) {
				return (
					<Box xcss={styles.filePreviewLoadingContainer}>
						<Spinner size="large" />
					</Box>
				);
			}
			return <Box as="img" src={imageBlobUrl} alt={fileName} xcss={styles.filePreviewImage} />;
		}
		if (kind === 'text') {
			if (isLoadingText) {
				return (
					<Box xcss={styles.filePreviewLoadingContainer}>
						<Spinner size="large" />
					</Box>
				);
			}
			return <Box xcss={styles.filePreviewTextContent}>{textContent}</Box>;
		}
		return (
			<Box xcss={styles.filePreviewFallback}>
				<Text size="medium" color="color.text.subtle">
					{formatMessage(filePreviewFallbackDescription)}
				</Text>
			</Box>
		);
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={onClose} width="x-large" shouldScrollInViewport>
					<ModalHeader hasCloseButton>
						<ModalTitle>{fileName}</ModalTitle>
					</ModalHeader>
					<ModalBody hasInlinePadding={false}>{renderBody()}</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
}
